/**
 * Server copy of the trip being edited, used as `existingPost` by every itinerary save so
 * fields the editor does not hold (cover photo, tags, location photos) are preserved.
 *
 * Saves used to `getPostById` before each POST, doubling the round trips. Now a save reads
 * this cache and only fetches when it is empty; after each save it is refreshed in the
 * background. It is filled only from full GETs, never from the save response, whose shape
 * is not guaranteed to carry those fields.
 *
 * Anything that writes the post outside saveItinerary (cover photo, categorization, adding
 * places from Inspo import) must call `invalidateExistingPostCache` so the next save fetches.
 */

import type Post from '@/models/post';
import { getPostById } from '@/api/itinerary';

let cached: { postId: string; post: Post } | null = null;
/** Bumped on every invalidation so an in-flight refresh started earlier is dropped. */
let version = 0;

export function getCachedExistingPost(postId: string): Post | null {
  return cached && cached.postId === postId ? cached.post : null;
}

/** Store a full post from a GET (e.g. the one the page loaded). */
export function setCachedExistingPost(post: Post | null | undefined): void {
  if (!post?._id || !Array.isArray(post.postLocations)) return;
  cached = { postId: String(post._id), post };
}

export function invalidateExistingPostCache(postId?: string | null): void {
  version += 1;
  if (!postId || cached?.postId === String(postId)) {
    cached = null;
  }
}

/** Fetch the post and cache it, unless the cache was invalidated while the GET ran. */
export async function refreshExistingPostCache(postId: string): Promise<Post> {
  const startedAt = version;
  const post = await getPostById(postId);
  if (version === startedAt) {
    setCachedExistingPost(post);
  }
  return post;
}
